import URLParamService from "@/services/URLParamService";
import { AuthConfig, AuthVersion, AuthVersionEnum } from "../types";
import { authVersions } from "../constants";

class authFactory {
  private static configs: Map<AuthVersion, AuthConfig> = new Map([
    [
      "v0",
      {
        version: "v0",
        endpoint: "/auth",
        requiredParams: ["session"],
      },
    ],
    [
      "v1",
      {
        version: "v1",
        endpoint: "/auth",
        requiredParams: ["session"],
      },
    ],
  ]);

  static getAuthVersion(): AuthVersion {
    const queryParams = URLParamService.getQueryParams();
    const version = queryParams.v as number | undefined;

    // Default to v0 (legacy) if no version specified or invalid version
    if (!version || !(version in authVersions)) {
      return AuthVersionEnum.V0;
    }

    return authVersions[version as keyof typeof authVersions];
  }

  static isMagicLink(): boolean {
    const queryParams = URLParamService.getQueryParams();
    return queryParams.authType === "magic-link";
  }

  static getAuthConfig(version?: AuthVersion): AuthConfig {
    const authVersion = version || this.getAuthVersion();
    const config = this.configs.get(authVersion);

    if (!config) {
      throw new Error(`No configuration found for auth version: ${authVersion}`);
    }

    const queryParams = URLParamService.getQueryParams();
    const magicLink = this.isMagicLink();
    const includedParams = magicLink
      ? [...config.requiredParams, "promoId"]
      : config.requiredParams;

    const payload = Object.fromEntries(
      Object.entries(queryParams).filter(([key]) => includedParams.includes(key))
    );

    return {
      ...config,
      endpoint: magicLink ? "/auth/magic-link" : config.endpoint,
      payload,
    };
  }

  static hasRequiredParams(version?: AuthVersion): boolean {
    const authVersion = version || this.getAuthVersion();
    const config = this.configs.get(authVersion);

    if (!config) {
      return false;
    }

    const queryParams = URLParamService.getQueryParams();
    const required = this.isMagicLink()
      ? [...config.requiredParams, "promoId"]
      : config.requiredParams;
    return required.every((param) => queryParams.hasOwnProperty(param) && queryParams[param]);
  }

  static getVersionPath(version?: AuthVersion): string {
    const authVersion = version || this.getAuthVersion();

    // v0 is legacy, no version path needed
    if (authVersion === AuthVersionEnum.V0) {
      return "";
    }

    // For v1, v2, etc., return "/v1", "/v2"
    return `/${authVersion}`;
  }
}

export default authFactory;
